/**
 * Yours (flag `yoursUI`, docs/yours.md): the open tasks that are a person's own to do, shown first
 * on the open board under their own heading, above Next up. Owner, 2026-10-06: "let's create a
 * system for displaying your tasks, so tasks that have been assigned to you, and tasks that you've
 * said you'll take, like I'll do it."
 *
 * A task is yours while it is open and either the roulette wheel picked you for it and the wheel is
 * unsettled (the task read carries `roulette_winner` and `roulette_stake` only then, docs/roulette.md
 * "Data"; the same condition as the card's stamp and the task screen's "Assigned to you") or you
 * hold a live "Me do it" claim on it (shared/claims.ts activeClaim: an hour, then it reads as
 * nobody). Each half follows its own flag, as the card does: a pick counts only while `rouletteUI`
 * is on, a claim only while `claimUI` is on, so a task in Yours always shows why on its card (the
 * stamp, the hand). Pure: both clients and the Node tests import it.
 */

import { activeClaim } from "./claims.ts";
import { sameName } from "./roulette.ts";
import type { Task } from "./types";

/** The heading over the group, in the board heading's style ("Next up" is the other). */
export const YOURS_HEADING = "Yours";

/** Which halves of the rule are on: the wheel's picks (rouletteUI) and "Me do it" claims (claimUI). */
export interface YoursFlags {
  roulette: boolean;
  claims: boolean;
}

/** The parts of a task the rule reads; every one optional so a board fixture of ids fits (a task without them is nobody's). */
export interface YoursFields {
  status?: Task["status"] | string;
  roulette_winner?: string | null;
  roulette_stake?: number | null;
  claimed_by?: string | null;
  claimed_at?: string | null;
}

/** Whether `task` is `me`'s to do right now: open, and the wheel's unsettled pick or a live claim of mine. */
export function isYours(task: YoursFields, me: string | null | undefined, now: number, flags: YoursFlags): boolean {
  if (task.status !== "open" || !me) return false;
  if (flags.roulette && task.roulette_winner && task.roulette_stake && sameName(task.roulette_winner, me)) return true;
  if (flags.claims) {
    const who = activeClaim({ status: "open", claimed_by: task.claimed_by, claimed_at: task.claimed_at }, now);
    if (who !== null && sameName(who, me)) return true;
  }
  return false;
}

/**
 * Whether `task` is someone else's to do right now (owner, 2026-10-12: "tasks assigned to others
 * aren't as relevant to you"): open, assigned by the rule above (the wheel's unsettled pick while
 * `rouletteUI` is on, a live "Me do it" claim while `claimUI` is on) and not yours. With no `me`
 * nothing is yours, so every assigned task is someone else's. With both flags off, nothing is.
 */
export function isOthers(task: YoursFields, me: string | null | undefined, now: number, flags: YoursFlags): boolean {
  if (task.status !== "open") return false;
  const picked = flags.roulette && !!task.roulette_winner && !!task.roulette_stake;
  const claimed = flags.claims && activeClaim({ status: "open", claimed_by: task.claimed_by, claimed_at: task.claimed_at }, now) !== null;
  return (picked || claimed) && !isYours(task, me, now, flags);
}

/** Who is looking and which halves count, for isOthers: the viewer, the clock, the two flags. */
export interface OthersOpts extends YoursFlags {
  me: string | null | undefined;
  now: number;
}

/**
 * The pile's order (docs/focus.md): the tasks someone else is on or was picked for sink below the
 * rest, each part in its own order (stable). Without `opts` nothing moves.
 */
export function sinkOthers<T extends YoursFields>(tasks: ReadonlyArray<T>, opts?: OthersOpts): T[] {
  if (!opts) return [...tasks];
  const mine: T[] = [];
  const others: T[] = [];
  for (const t of tasks) (isOthers(t, opts.me, opts.now, opts) ? others : mine).push(t);
  return [...mine, ...others];
}

/**
 * The open board split into what is yours and the rest, each in the board's own order (the group
 * reorders nothing within itself: an urgent task of yours still comes before a plain one). With the
 * flag off, or nothing yours, `yours` is empty and `rest` is the whole list.
 */
export function splitYours<T extends YoursFields>(tasks: ReadonlyArray<T>, me: string | null | undefined, now: number, flags: YoursFlags & { on: boolean }): { yours: T[]; rest: T[] } {
  if (!flags.on || !me) return { yours: [], rest: [...tasks] };
  const yours: T[] = [];
  const rest: T[] = [];
  for (const t of tasks) (isYours(t, me, now, flags) ? yours : rest).push(t);
  return { yours, rest };
}

/**
 * A key that changes only when the group's membership does (the ids in order), so a board that
 * lays itself out once per answer can lay out again when a claim lapses without doing so on every
 * minute tick.
 */
export function yoursKey(yours: ReadonlyArray<{ id: string }>): string {
  return yours.map((t) => t.id).join(",");
}

/**
 * Whether the rest of the board wears the Next up heading: Next up's own rule (something in full),
 * or Yours above it with anything after, so the group has a visible end (a card under Yours with no
 * heading over it would read as yours too).
 */
export function restHeading(nextUpHeading: boolean, yoursCount: number, restCount: number): boolean {
  return nextUpHeading || (yoursCount > 0 && restCount > 0);
}
